import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Detects the class of errors caused by a stale `index.html` (served by a CDN
 * after a redeploy) referencing JS/CSS chunks whose hashed filenames no longer
 * exist — i.e. ChunkLoadError / failed dynamic import. These are recoverable by
 * fetching a fresh document, so we auto-reload once instead of leaving the user
 * stranded on the error screen.
 */
function isChunkLoadError(error) {
  if (!error) return false;
  const message = error.message || '';
  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

const RELOAD_GUARD_KEY = 'corvana_boundary_chunk_reload';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.handleHardReload = this.handleHardReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ errorInfo });

    // Auto-recover from stale-chunk errors by reloading once with a
    // cache-busting query so the browser/CDN fetch a fresh index.html.
    if (isChunkLoadError(error)) {
      let alreadyReloaded = null;
      try { alreadyReloaded = window.sessionStorage.getItem(RELOAD_GUARD_KEY); } catch { /* ignore */ }
      if (!alreadyReloaded) {
        try { window.sessionStorage.setItem(RELOAD_GUARD_KEY, '1'); } catch { /* ignore */ }
        const url = new URL(window.location.href);
        url.searchParams.set('cb', Date.now().toString());
        window.location.replace(url.toString());
      }
    } else {
      // Non-chunk error recovered cleanly — clear the guard for next time.
      try { window.sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* ignore */ }
    }
  }

  handleHardReload() {
    // A real reload (not just a state reset) so a fresh document + chunks are
    // fetched. State-only reset re-hits the same broken dynamic import.
    try { window.sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* ignore */ }
    const url = new URL(window.location.href);
    url.searchParams.set('cb', Date.now().toString());
    window.location.replace(url.toString());
  }

  render() {
    if (this.state.hasError) {
      const chunkError = isChunkLoadError(this.state.error);
      return (
        <div className="min-h-[300px] flex items-center justify-center p-8" data-testid="app-error-boundary">
          <div className="text-center max-w-md">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">
              {chunkError ? 'Updating to the latest version…' : 'Something went wrong'}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {chunkError
                ? 'A new version was just released. Reloading now to get the latest files.'
                : (this.props.fallbackMessage || 'This section encountered an error. Please try refreshing.')}
            </p>
            <button
              onClick={this.handleHardReload}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
              data-testid="app-error-boundary-reload"
            >
              <RefreshCw className="w-4 h-4" />
              Reload
            </button>
            {!chunkError && this.state.error && (
              <details className="mt-6 text-left" data-testid="app-error-boundary-details">
                <summary className="cursor-pointer text-xs text-muted-foreground select-none">
                  Technical details
                </summary>
                <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted/50 p-3 text-[11px] leading-relaxed text-muted-foreground">
{String(this.state.error && (this.state.error.stack || this.state.error.message || this.state.error))}
{this.state.errorInfo && this.state.errorInfo.componentStack
  ? '\n--- Component stack ---' + this.state.errorInfo.componentStack
  : ''}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
